import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { 
  LayoutDashboard, 
  Users, 
  Bus, 
  MapPin, 
  Calendar, 
  CreditCard, 
  LogOut, 
  Menu, 
  X, 
  ChevronDown,
  User as UserIcon,
  Settings,
  Bell,
  GraduationCap,
  QrCode,
  Building,
  CheckCircle2,
  Clock,
  Landmark,
  ShieldCheck,
  ChevronLeft,
  FileText,
  Database,
  Package,
  Receipt,
  Tag,
  Wallet,
  PlusCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/index.jsx';

const DashboardLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [openGroupId, setOpenGroupId] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const { user, logout, selectedYear, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuStructure = [
    { title: 'لوحة التحكم', icon: LayoutDashboard, path: '/dashboard', module: 'dashboard' },
    { title: 'الطلاب', icon: Users, path: '/dashboard/students', module: 'students' },
    
    // Group 1: Facilities Management
    {
      title: 'إدارة المرافق',
      icon: Building,
      id: 'facilities',
      children: [
        { title: 'الأقسام', icon: GraduationCap, path: '/dashboard/school-classes', module: 'school-classes' },
        { title: 'استعمال الزمن', icon: Clock, path: '/dashboard/timetables', module: 'school-classes' },
        { title: 'المجموعات', icon: Users, path: '/dashboard/groups', module: 'groups' },
        { title: 'المؤسسات', icon: Building, path: '/dashboard/institutions', module: 'institutions' },
        { title: 'الحافلات', icon: Bus, path: '/dashboard/buses', module: 'buses' },
        { title: 'السائقين', icon: UserIcon, path: '/dashboard/drivers', module: 'drivers' },
        { title: 'الشركاء', icon: Building, path: '/dashboard/partners', module: 'partners' },
        { title: 'الموردين', icon: Package, path: '/dashboard/suppliers', module: 'partners' }, // Using partners module as fallback
        { title: 'اعضاء الجمعية', icon: ShieldCheck, path: '/dashboard/staff', module: 'staff' },
        { title: 'المناطق', icon: MapPin, path: '/dashboard/regions', module: 'regions' },
        { title: 'الانضباط والأرشيف', icon: FileText, path: '/dashboard/discipline', module: 'students' },
        { title: 'أنواع المصروفات', icon: Tag, path: '/dashboard/expense-types', module: 'settings' },
      ]
    },

    // Group 2: Finance
    {
      title: 'الإدارة المالية',
      icon: CreditCard,
      id: 'finance',
      children: [
        { title: 'المدفوعات', icon: CreditCard, path: '/dashboard/payments', module: 'payments' },
        { title: 'تقارير الإيداع', icon: FileText, path: '/dashboard/deposit-reports', module: 'payments' },
        { title: 'البنك', icon: Landmark, path: '/dashboard/bank', module: 'payments' },
        { title: 'المدفوعات النقدية', icon: Wallet, path: '/dashboard/cash-payments', module: 'payments' },
        { title: 'المصروفات', icon: Receipt, path: '/dashboard/expenses', module: 'payments' },
      ]
    },


    { title: 'إحصائيات والتقارير', icon: FileText, path: '/dashboard/financial-reports', module: 'payments' },
    { title: 'سجل الحضور', icon: CheckCircle2, path: '/dashboard/attendance', module: 'attendance' },
    { title: 'السنوات الدراسية', icon: Calendar, path: '/dashboard/academic-years', module: 'academic-years' },
    { title: 'الإعدادات', icon: Settings, path: '/dashboard/settings', module: 'settings' },
  ];

  // Logic to keep relevant group open based on current path
  useEffect(() => {
    const findAndOpenGroup = () => {
      menuStructure.forEach(item => {
        if (item.children) {
          const isActive = item.children.some(child => location.pathname === child.path);
          if (isActive) {
            setOpenGroupId(item.id);
          }
        }
      });
    };
    findAndOpenGroup();
  }, [location.pathname]);

  const toggleGroup = (id) => {
    setOpenGroupId(prevId => prevId === id ? null : id);
  };

  const checkItemVisibility = (item) => {
    if (!item.module) return true;
    if (item.module === 'settings') return user?.role === 'Admin';
    return hasPermission(item.module, 'read');
  };

  const filteredMenu = menuStructure.map(item => {
    if (item.children) {
      const visibleChildren = item.children.filter(checkItemVisibility);
      if (visibleChildren.length === 0) return null;
      return { ...item, children: visibleChildren };
    }
    return checkItemVisibility(item) ? item : null;
  }).filter(Boolean);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async (shouldBackup = false) => {
    if (shouldBackup) {
      setIsBackingUp(true);
      try {
        const response = await api.get('/backup', { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const now = new Date();
        const timestamp = now.toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0];
        const filename = `backup-${timestamp}.sql`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error("Backup failed:", error);
        alert("فشل عمل النسخ الاحتياطي. سيتم تسجيل الخروج بدون نسخة احتياطية.");
      } finally {
        setIsBackingUp(false);
      }
    }
    
    await logout();
    navigate('/login');
  };

  return (
    <div className="h-screen bg-slate-50 flex font-sans text-slate-800 overflow-hidden" dir="rtl">
      {/* Logout Confirmation Modal */}
      <Modal 
        isOpen={showLogoutModal} 
        onClose={() => !isBackingUp && setShowLogoutModal(false)}
        title="تأكيد تسجيل الخروج"
      >
        <div className="p-1">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500 shrink-0">
              <LogOut className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">هل أنت متأكد من الخروج؟</h3>
              <p className="text-sm text-slate-500">يمكنك عمل نسخة احتياطية لقاعدة البيانات قبل المغادرة.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {hasPermission('backup', 'read') && (
              <Button 
                onClick={() => handleLogoutConfirm(true)} 
                isLoading={isBackingUp}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-sm"
              >
                <Database className="w-4 h-4 ml-2" />
                نسخ احتياطي وتسجيل الخروج
              </Button>
            )}
            
            <Button 
              variant="outline"
              onClick={() => handleLogoutConfirm(false)}
              disabled={isBackingUp}
              className="w-full h-12 text-sm border-slate-200 text-slate-600"
            >
              تسجيل الخروج فقط
            </Button>

            <button 
              onClick={() => setShowLogoutModal(false)}
              disabled={isBackingUp}
              className="text-xs text-slate-400 hover:text-slate-600 mt-2 font-medium"
            >
              إلغاء
            </button>
          </div>
        </div>
      </Modal>
      {/* Sidebar Overlay for Mobile */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside 
        className={`fixed lg:sticky top-0 right-0 h-full bg-white border-l border-slate-200 z-50 transition-all duration-300 flex flex-col
          ${isSidebarOpen ? 'w-60' : 'w-20'} 
          ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`}
      >

        <div className="border-b border-slate-100 flex-shrink-0">
          <div className="h-16 flex items-center justify-between px-4">
            {isSidebarOpen ? (
                <Link to="/dashboard" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
                  <img src="/logo.png" alt="" className="w-9 h-9 rounded-lg object-contain shrink-0" />
                  <div className="flex flex-col">
                    <span className="font-bold text-[11px] text-slate-800 leading-tight">جمعية تنمية النقل المدرسي</span>
                    <span className="text-[9px] text-slate-500 font-bold">أولاد دحو</span>
                  </div>
                </Link>
            ) : (
                <Link to="/dashboard" className="mx-auto hover:opacity-80 transition-opacity">
                  <img src="/logo.png" alt="" className="w-9 h-9 rounded-lg object-contain" />
                </Link>
            )}
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>


        <nav className="p-3 flex flex-col gap-1.5 h-[calc(100%-64px)] overflow-y-auto">
          <div className="flex-1 space-y-1">
            {filteredMenu.map((item, idx) => {
              if (item.children) {
                const isOpen = openGroupId === item.id;
                const hasActiveChild = item.children.some(child => location.pathname === child.path);

                return (
                  <div key={item.id || idx} className="mb-1">
                    <button
                      onClick={() => toggleGroup(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 group
                        ${hasActiveChild && !isOpen ? 'bg-primary/5 text-primary' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                      <item.icon className={`w-5 h-5 shrink-0 transition-colors ${hasActiveChild ? 'text-primary' : 'text-slate-400 group-hover:text-slate-600'}`} />
                      {isSidebarOpen && (
                        <>
                          <span className="text-[13px] font-medium flex-1 text-right">{item.title}</span>
                          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                        </>
                      )}
                    </button>

                    <AnimatePresence>
                      {isOpen && isSidebarOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="overflow-hidden mr-4 pr-3 border-r border-slate-100 mt-1 flex flex-col gap-1"
                        >
                          {item.children.map((child) => {
                            const isActive = location.pathname === child.path;
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 group
                                  ${isActive 
                                    ? 'text-primary font-semibold' 
                                    : 'text-slate-500 hover:text-slate-800'}`}
                              >
                                <child.icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-primary' : 'text-slate-300 group-hover:text-slate-400'}`} />
                                <span className="text-[12.5px]">{child.title}</span>
                              </Link>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 group
                    ${isActive 
                      ? 'bg-primary/10 text-primary font-bold shadow-sm shadow-primary/5' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                >
                  <item.icon className={`w-5 h-5 shrink-0 transition-colors ${isActive ? 'text-primary' : 'text-slate-400 group-hover:text-slate-600'}`} />
                  {isSidebarOpen && <span className="text-[13px] whitespace-nowrap">{item.title}</span>}
                  {isActive && (
                    <motion.div 
                        layoutId="active-pill" 
                        className="absolute right-0 top-2 bottom-2 w-1 bg-primary rounded-l-full" 
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* User Section in Sidebar bottom */}
          <div className="border-t border-slate-100 pt-4 mt-auto">
             <button 
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 hover:bg-red-50 hover:text-red-500 transition-all duration-200 group"
             >
                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                    <LogOut className="w-4 h-4 shrink-0" />
                </div>
                {isSidebarOpen && <span className="text-sm font-medium">تسجيل الخروج</span>}
             </button>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 transition-all duration-300 overflow-hidden">
        {/* Header */}
       <header className="h-16 bg-white border-b-2 border-slate-100 px-4 md:px-6 flex items-center justify-between sticky top-0 z-30 flex-shrink-0">
  
  {/* الجزء الأيمن: القائمة والاسم */}
  <div className="flex items-center gap-4">
    <button 
      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-all"
    >
      <Menu className="w-5 h-5" />
    </button>
    
    <div className="hidden sm:flex flex-col border-r border-slate-100 pr-4">
      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">النظام الحالي</span>
      <h1 className="text-sm font-semibold text-slate-700">مرحباً، {user?.name || 'الآدمن'}</h1>
    </div>

    {/* Selected Year Display */}
    <motion.div 
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate('/select-year')}
      className="flex items-center gap-2 px-2 md:px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-xl cursor-pointer hover:bg-blue-100 transition-all mr-2 group"
    >
      <div className="p-1.5 bg-white rounded-lg shadow-sm text-blue-600 group-hover:text-blue-700 transition-colors">
        <Calendar className="w-4 h-4" />
      </div>
      <div className="hidden md:flex flex-col items-start pr-1">
        <span className="text-[9px] text-blue-400 font-bold uppercase leading-none mb-0.5">السنة المفتوحة</span>
        <span className="text-xs font-bold text-blue-700 leading-none">{selectedYear?.name || 'لم يتم الاختيار'}</span>
      </div>
    </motion.div>
  </div>

  {/* الجزء الأوسط/الأيسر: الأزرار والبروفايل */}
  <div className="flex items-center gap-2 md:gap-3">
    
    {/* أزرار العمليات - نمط مسطح بحدود */}
    <div className="flex items-center gap-2">
      {hasPermission('attendance', 'create') && (
        <button 
          onClick={() => navigate('/dashboard/attendance/scanner')}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-green-200 bg-green-50/50 text-green-700 hover:bg-green-100 transition-colors"
        >
          <Clock className="w-4 h-4" />
          <span className="text-xs font-bold hidden lg:inline-block">تسجيل حضور</span>
        </button>
      )}

      {hasPermission('payments', 'create') && (
        <button 
          onClick={() => navigate('/dashboard/scanner')}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-primary/20 bg-primary/5 text-primary hover:bg-primary hover:text-white transition-all"
        >
          <QrCode className="w-4 h-4" />
          <span className="text-xs font-bold hidden lg:inline-block">قراءة رمز QR</span>
        </button>
      )}
    </div>

    {/* فاصل بسيط */}
    <div className="h-8 w-px bg-slate-200 mx-1"></div>

    {/* منطقة المستخدم - تصميم نظيف ومحدد */}
    <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 py-1 px-1 pl-3 rounded-lg group">
      <div className="w-9 h-9 rounded-md bg-white border border-slate-200 flex items-center justify-center text-slate-400">
        <UserIcon className="w-5 h-5" />
      </div>
      
      <div className="text-right hidden md:block">
        <p className="text-xs font-bold text-slate-800 leading-tight">{user?.name}</p>
        <p className="text-[10px] text-slate-500 font-medium">{user?.role || 'مسؤول النظام'}</p>
      </div>
    </div>

  </div>
</header>

        <main className="flex-1 p-4 md:p-6 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="mx-auto max-w-7xl pb-10"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
